// Maps the backend's carrier-failure categories (tickets.carrier_failure /
// carriers conversation failure_diagnosis) to localized, actionable texts.
// Categories are produced by the mail service's classify_carrier_failure().

const KNOWN_CATEGORIES = [
  'missing_credentials',
  'client_not_on_account',
  'invalid_credentials',
  'parcel_not_found_on_account',
  'shop_account_not_found',
  'missing_email_fallback',
  'claim_missing_on_portal',
  'claim_already_exists',
  'remark_not_available',
  'claim_closed_on_portal',
  'claim_locked_on_portal',
  'portal_refused',
  'portal_refused_too_early',
  'captcha_blocked',
  'portal_timeout',
  'technical_error',
  'otp_fetch_timeout',
  'otp_mailbox_error',
  'otp_enrollment_required',
  'client_unresolved',
  'validation_error',
  'missing_document',
  'portal_flow',
  'motif_invalid',
  'parameter_invalid',
  'claim_invisible_no_reply',
  'email_undeliverable',
  'claim_not_found_for_update',
  'attachment_upload_failed',
];

const DEFAULTS = {
  missing_credentials: {
    title: 'Carrier credentials missing',
    explanation: 'No {{carrier}} credentials are configured for this ticket’s client.',
    action: 'Add the credentials in the Carriers menu, then approve the message again.',
  },
  // The ticket's client is on none of the tenant's accounts for the carrier
  // (every account reserved to other clients): the fix is to attach it to
  // one of the contracts — the bell shows the whole sentence.
  client_not_on_account: {
    title: 'Client {{client}} not attached to a {{carrier}} account',
    explanation: 'The client {{client}} is attached to none of your {{carrier}} accounts.',
    action: 'Add it to one of your {{carrier}} contracts (Carriers menu › {{carrier}} › Portal accounts), then relaunch the request.',
    notifBody: 'The client {{client}} is attached to none of your {{carrier}} accounts. Add it to one of your {{carrier}} contracts (Carriers menu › {{carrier}} › Portal accounts), then relaunch the request.',
    settingsLink: 'Open the {{carrier}} accounts',
  },
  invalid_credentials: {
    title: 'Carrier credentials rejected',
    explanation: 'The {{carrier}} portal rejects the configured credentials (wrong or expired password).',
    action: 'Update the credentials in the Carriers menu, then approve the message again.',
  },
  attachment_upload_failed: {
    title: 'Carrier files not attached',
    explanation: 'The {{carrier}} portal did not show every file of the message after the upload: the message was not posted, nothing reached the carrier.',
    action: 'Send the files to {{carrier}} another way (e-mail with the files) or attach them by hand on the portal, then post the message.',
  },
  email_undeliverable: {
    title: 'Carrier e-mail not delivered',
    explanation: 'The {{carrier}} address rejected the message (unknown or wrong address): nothing reached the carrier.',
    action: 'Fix the {{carrier}} e-mail address in the Carriers menu, then approve the message again.',
  },
  claim_invisible_no_reply: {
    title: 'Claim confirmed by the portal but not visible',
    explanation: 'The {{carrier}} portal reports an existing claim for this parcel, yet the parcel page shows no dossier and no answer arrived.',
    action: 'Check the claim on the {{carrier}} portal by hand (another reference or another account?).',
  },
  claim_not_found_for_update: {
    title: 'No claim under this tracking number',
    explanation: 'The {{carrier}} portal has no claim for this conversation’s tracking number on the account: the claim was filed under another number, or never registered.',
    action: 'Check the tracking number in the request parameters (it must be the one the claim was filed with), correct it, then approve again.',
  },
  parcel_not_found_on_account: {
    title: 'Parcel not visible on the carrier account',
    explanation: 'The parcel is not visible from the {{carrier}} account used — it probably belongs to another contract (client-specific account).',
    action: 'Check the {{carrier}} account mapped to this client or add its credentials, then approve again.',
  },
  shop_account_not_found: {
    title: 'Client not found on the carrier portal',
    explanation: 'The client’s name does not match any account on the {{carrier}} portal.',
    action: 'Check the client’s label, or tell us the exact portal account name to use.',
  },
  missing_email_fallback: {
    title: 'Email fallback not configured',
    explanation: 'The portal refused the request and no email (SMTP) configuration exists to send it by email instead.',
    action: 'Add the carrier’s email address in the Carriers menu (Emails field), then approve again — the request will go out by email.',
  },
  claim_missing_on_portal: {
    title: 'Claim absent from the portal',
    explanation: 'The claim looked created but does not exist on the {{carrier}} portal.',
    action: 'Approve the message again to re-file the claim.',
  },
  claim_already_exists: {
    title: 'Claim already in progress',
    explanation: 'The {{carrier}} portal reports that a claim is already open for this parcel.',
    action: 'Follow the existing claim on the portal; a new one cannot be filed.',
  },
  remark_not_available: {
    title: 'Claim being registered by the carrier',
    explanation: 'The {{carrier}} portal does not yet accept updates on this parcel — the claim is still being registered on the carrier’s side (this usually takes one to a few days).',
    action: 'Nothing to do: the message will be re-sent automatically in 2 days. If it still fails after the automatic retries, report this ticket to the Unanimity team.',
  },
  claim_locked_on_portal: {
    title: 'Claim locked by the carrier (cancellation in progress)',
    explanation: 'The {{carrier}} portal refuses any comment on this claim: a cancellation request is in progress on the carrier’s side. Claims in this state have not accepted a follow-up again.',
    action: 'The follow-up was not sent and will not be retried automatically. If the customer still needs an answer, write to {{carrier}} by e-mail, or check the claim on the portal.',
  },
  claim_closed_on_portal: {
    title: 'Claim closed by the carrier',
    explanation: 'The {{carrier}} portal shows this claim as closed (see the carrier’s closure message on the conversation). No message can be posted on a closed claim; when a carrier contact e-mail is configured, the follow-up was sent by e-mail instead.',
    action: 'Read the closure message. If you still need to contact {{carrier}} about this parcel, send it by e-mail or open a new claim.',
  },
  portal_refused: {
    title: 'Carrier portal refused the request',
    explanation: 'The {{carrier}} portal refused to create the request — usually a temporary error on the carrier’s side.',
    action: 'Approve again to retry; if the refusal persists, report this ticket to the Unanimity team.',
  },
  portal_refused_too_early: {
    title: 'Claim not accepted yet (delay)',
    explanation: 'The {{carrier}} portal does not accept this claim yet: the parcel has not left the carrier’s agency, or its delivery delay has not passed. Nothing was filed and no e-mail was sent.',
    action: 'Nothing to do: the AI agent files the request again after its wake-up.',
  },
  captcha_blocked: {
    title: 'Blocked by a captcha',
    explanation: 'The {{carrier}} portal blocked the automation with a captcha.',
    action: 'Approve again to retry; if it persists, report this ticket to the Unanimity team.',
  },
  portal_timeout: {
    title: 'Carrier portal unresponsive',
    explanation: 'The {{carrier}} portal did not respond in time.',
    action: 'Approve again to retry; if it persists, report this ticket to the Unanimity team.',
  },
  technical_error: {
    title: 'Carrier contact failed (technical error)',
    explanation: 'The automation failed on the {{carrier}} portal for a technical reason.',
    action: 'Report this ticket to the Unanimity team for analysis.',
  },
  otp_fetch_timeout: {
    title: 'Verification code not received in time',
    explanation: 'The {{carrier}} portal asked for an email verification code, but none arrived in the connected OTP mailbox in time.',
    action: 'Nothing to do for now: it will be retried automatically. If it keeps failing, check that the mailbox connected on the carrier page is the one receiving the portal’s codes.',
  },
  client_unresolved: {
    title: 'No client identified on the ticket',
    explanation: 'The ticket has no identified client, so the {{carrier}} account to use could not be selected.',
    action: 'Choose the client from the client chip at the top of the ticket, then approve the message again.',
  },
  validation_error: {
    title: 'Request information missing',
    explanation: 'The {{carrier}} request is missing required information (comment, product family…), so it cannot be filed as-is.',
    action: 'Complete the message/parameters on the conversation, then approve again.',
  },
  missing_document: {
    title: 'Document required by the carrier',
    explanation: 'The {{carrier}} portal requires a document of a given type for this request (e.g. the sales invoice as « Facture ») and the ticket does not contain one.',
    action: 'Add the document to the ticket (or ask the customer for it), then approve the message again.',
  },
  portal_flow: {
    title: 'Carrier portal does not offer this request',
    explanation: 'The {{carrier}} portal does not offer the requested option for this parcel (request type or motif unavailable).',
    action: 'Open the parcel on the carrier portal to check what is possible, or contact the carrier another way.',
  },
  motif_invalid: {
    title: 'Claim motive not accepted by the carrier',
    explanation: 'The claim motive sent to the {{carrier}} portal is not one of the motives it offers for this parcel.',
    action: 'Edit the request with one of the portal motives (see the technical detail), then approve the message again.',
  },
  parameter_invalid: {
    title: 'Parameter refused by the carrier portal for this parcel',
    explanation: 'The {{carrier}} portal does not offer the requested value for this parcel; the options depend on the parcel and its state.',
    action: 'Pick one of the options the portal offers (listed below), then approve the message again.',
  },
  otp_enrollment_required: {
    title: 'Two-factor enrollment required on the carrier portal',
    explanation: 'The {{carrier}} portal now requires two-factor authentication for this account and no longer lets the login skip it, so no automatic connection is possible.',
    action: 'One-time action by the account holder: log in to the {{carrier}} portal, choose the « E-mail » method with a mailbox you can connect, then connect that mailbox on the carrier page (Carriers > {{carrier}} > OTP mailbox) and approve again.',
  },
  otp_mailbox_error: {
    title: 'OTP mailbox connection problem',
    explanation: 'The {{carrier}} portal asked for an email verification code, but the connected OTP mailbox could not be used (expired app password or Outlook access, or the code was refused).',
    action: 'Reconnect the OTP mailbox on the carrier page (Carriers > {{carrier}} > OTP mailbox), then approve again.',
  },
};

const OBJECT_ID_RE = /^[a-f0-9]{24}$/;

// `details`: the failure's details (conversation failure_diagnosis.details or
// the notification data) — the tenant-configured client label, the tenant's
// carrier label and the carrier page id when the mail service recorded them.
export function getCarrierFailureTexts(t, category, carrier, details = {}) {
  const safeCategory = KNOWN_CATEGORIES.includes(category) ? category : 'technical_error';
  const defaults = DEFAULTS[safeCategory];
  const info = details || {};
  const vars = {
    carrier: info.carrier_label || carrier || t('carrierFailure.genericCarrier', 'the carrier'),
    client: info.client_name || t('carrierFailure.genericClient', 'of this ticket'),
  };
  const carrierId = String(info.carrier_id || '');
  return {
    category: safeCategory,
    title: t(`carrierFailure.${safeCategory}.title`, defaults.title, vars),
    explanation: t(`carrierFailure.${safeCategory}.explanation`, defaults.explanation, vars),
    action: t(`carrierFailure.${safeCategory}.action`, defaults.action, vars),
    // The whole sentence for the bell when the category's title alone does not
    // say what to do; null otherwise (the bell shows the title).
    notifBody: defaults.notifBody ? t(`carrierFailure.${safeCategory}.notifBody`, defaults.notifBody, vars) : null,
    // The carrier settings page where the operator fixes it, when known.
    settingsPath: defaults.settingsLink && OBJECT_ID_RE.test(carrierId) ? `/carriers-instructions/${carrierId}` : null,
    settingsLink: defaults.settingsLink ? t(`carrierFailure.${safeCategory}.settingsLink`, defaults.settingsLink, vars) : null,
    // Shown once the automatic retries are spent: the category's own advice
    // when it has one, otherwise the generic "approve again / handle on the
    // portal" instruction (re-approval restarts the retry ladder server-side).
    exhaustedAction: t(
      `carrierFailure.${safeCategory}.exhaustedAction`,
      t(
        'carrierFailure.retriesExhaustedAction',
        'Approve the message again to start a new cycle of automatic retries, or handle the request directly on the carrier portal.',
        vars
      ),
      vars
    ),
  };
}
